import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { History, Loader2, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HistoryPage() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }

    axios.get(`${API_URL}/api/analyses`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setAnalyses(res.data)).finally(() => setLoading(false));
  }, [token, navigate]);

  const verdictColors = {
    probably_authentic: 'text-emerald-400 bg-emerald-400/10',
    probably_fake: 'text-rose-400 bg-rose-400/10',
    uncertain: 'text-amber-400 bg-amber-400/10'
  };

  return (
    <div className="min-h-screen hero-gradient pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold text-white mb-8">{t('history_title')}</h1>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : analyses.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center">
            <History className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">{t('history_empty')}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium"
            >
              {t('history_start')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => (
              <motion.div
                key={analysis.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="glass rounded-xl p-4 flex items-center justify-between hover:bg-white/5 transition-colors cursor-pointer"
                onClick={() => navigate(`/analysis/${analysis.id}`)}
              >
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${verdictColors[analysis.verdict] || verdictColors.uncertain}`}>
                    {analysis.confidence_score}%
                  </div>
                  <div>
                    <p className="text-white font-medium capitalize">{analysis.content_type}</p>
                    <p className="text-slate-400 text-sm">
                      {new Date(analysis.created_at).toLocaleDateString()} &bull;
                      {analysis.metadata?.llms_used?.length || 1} LLM(s) &bull;
                      {analysis.metadata?.credits_used || 1} {t('credits')}
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
